import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { TelegramLogo, CheckCircle, Warning, Copy, PaperPlaneTilt, CircleNotch, Info, DeviceMobile, Export, Plus, BellRinging, Star, TrendDown, House, Envelope as EnvelopeIcon, Clock, Eye } from "@phosphor-icons/react";
import { Slider } from "@/components/ui/slider";
import PwaInstallButton from "@/components/PwaInstallButton";

export default function Impostazioni() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [chatId, setChatId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [serverConfigured, setServerConfigured] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [prefs, setPrefs] = useState({
    watchlist_confirmation: true,
    new_auction: true,
    new_listing: false,
    discount_increased: true,
    min_discount_increase_pct: 5,
    digest_enabled: false,
    digest_hour: 20,
  });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const standalone = (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)
      || window.navigator.standalone === true;
    setIsStandalone(standalone);
    setIsIos(/iPad|iPhone|iPod/i.test(window.navigator.userAgent || ""));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings");
      setChatId(data.telegram_chat_id || "");
      setEnabled(!!data.telegram_enabled);
      setServerConfigured(!!data.telegram_server_configured);
      if (data.alert_prefs) setPrefs((p) => ({ ...p, ...data.alert_prefs }));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const savePrefs = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    setSavingPrefs(true);
    try {
      await api.put("/settings/alert-prefs", patch);
    } catch (e) {
      toast.error("Errore salvataggio preferenze");
    } finally { setSavingPrefs(false); }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/alerts/digest/preview");
      setPreview(data);
    } catch (e) {
      toast.error("Errore anteprima digest");
    } finally { setPreviewLoading(false); }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/alerts/digest/send");
      if (data.sent) {
        toast.success(`Digest inviato · ${data.total_events} eventi`);
      } else {
        toast.info("Nessun nuovo evento da riportare in questo momento");
      }
      setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio digest fallito");
    } finally { setSendingDigest(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/telegram", { chat_id: chatId.trim(), enabled });
      toast.success("Impostazioni Telegram salvate");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const test = async () => {
    if (!chatId.trim()) { toast.error("Inserisci prima il tuo chat_id"); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/settings/telegram/test");
      if (data.mocked) {
        toast.info("Test simulato (console log). Aggiungi TELEGRAM_BOT_TOKEN al server per l'invio reale.", { duration: 6000 });
      } else {
        toast.success("Messaggio di test inviato · controlla Telegram");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test fallito");
    } finally { setTesting(false); }
  };

  const copyBot = () => {
    navigator.clipboard.writeText("@userinfobot");
    toast.info("@userinfobot copiato — apri Telegram e inviagli un messaggio qualsiasi");
  };

  const copyImmoBot = () => {
    navigator.clipboard.writeText("@ImmoRux_Bot");
    toast.info("@ImmoRux_Bot copiato — apri Telegram e invia /start");
  };

  return (
    <div className="min-h-[calc(100vh-56px)] bg-zinc-950">
      <div className="border-b border-white/10 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="font-mono-data text-xs text-lime-400 tracking-widest mb-2">— IMPOSTAZIONI</div>
          <h1 className="font-display font-black text-3xl">Notifiche & integrazioni</h1>
          <p className="text-sm text-zinc-400 mt-2">Configura come ImmoScope ti avvisa quando trova nuove occasioni.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {/* Telegram Card */}
        <div className="border border-white/10 bg-zinc-900/30" data-testid="telegram-settings-card">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#229ED9]/10 border border-[#229ED9]/30">
                <TelegramLogo size={20} weight="fill" className="text-[#229ED9]" />
              </div>
              <div>
                <div className="font-display font-bold text-lg">Alert Telegram</div>
                <div className="text-xs text-zinc-500">Conferma sui preferiti salvati + alert sulle nuove aste</div>
              </div>
            </div>
            {enabled && chatId ? (
              <Badge className="rounded-none bg-lime-400 text-lime-950 font-mono-data text-[10px] hover:bg-lime-400" data-testid="tg-status-active">
                <CheckCircle size={10} weight="bold" className="mr-1" /> ATTIVO
              </Badge>
            ) : (
              <Badge className="rounded-none bg-zinc-800 text-zinc-400 font-mono-data text-[10px] hover:bg-zinc-800" data-testid="tg-status-off">
                DISATTIVO
              </Badge>
            )}
          </div>

          {!serverConfigured && (
            <div className="p-4 border-b border-white/10 bg-amber-500/[0.05] flex gap-3 items-start" data-testid="tg-warning-server">
              <Warning size={18} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
              <div className="text-sm text-zinc-300">
                <b className="text-amber-400">Modalità simulata.</b> Il server non ha ancora un token bot configurato: i messaggi vengono registrati in console. Per l'invio reale l'amministratore deve aggiungere <code className="text-lime-400 bg-black/40 px-1 py-0.5 font-mono-data text-xs">TELEGRAM_BOT_TOKEN</code> in <code className="font-mono-data text-xs">backend/.env</code>.
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="p-5 border-b border-white/10 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Info size={14} weight="bold" className="text-cyan-400" />
              <span className="font-mono-data text-[10px] text-cyan-400 tracking-widest">COME COLLEGARE TELEGRAM · 2 MINUTI</span>
            </div>
            <ol className="text-sm text-zinc-300 space-y-3 list-decimal list-inside marker:text-lime-400">
              {serverConfigured && (
                <li className="pl-1">
                  <span className="text-lime-400 font-bold">Passo obbligatorio:</span> apri Telegram e avvia il nostro bot cliccando <a href="https://t.me/ImmoRux_Bot" target="_blank" rel="noopener noreferrer" className="text-lime-400 hover:underline font-mono-data" data-testid="link-immo-bot">@ImmoRux_Bot</a> → <b>/start</b>. <button onClick={copyImmoBot} className="text-zinc-500 hover:text-lime-400 ml-1 inline-flex items-center gap-1"><Copy size={11} weight="bold" /></button>
                  <div className="text-[11px] text-zinc-500 mt-0.5 ml-1">Senza questo passaggio Telegram blocca i messaggi in arrivo dal bot.</div>
                </li>
              )}
              <li className="pl-1">
                Cerca <button onClick={copyBot} className="text-lime-400 hover:underline inline-flex items-center gap-1 font-mono-data" data-testid="btn-copy-userinfobot">@userinfobot <Copy size={11} weight="bold" /></button> e invia un messaggio qualsiasi (es. "ciao")
              </li>
              <li className="pl-1">
                Il bot ti risponderà con il tuo <code className="text-lime-400 bg-black/40 px-1 font-mono-data text-xs">Id</code> — un numero come <span className="font-mono-data text-cyan-400">123456789</span>
              </li>
              <li className="pl-1">
                Incolla quel numero nel campo qui sotto, attiva la notifica e clicca <b>Invia test</b> per confermare
              </li>
            </ol>
          </div>

          {/* Form */}
          <div className="p-5 space-y-4">
            <div>
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">CHAT ID TELEGRAM</Label>
              <Input
                value={chatId}
                onChange={(e) => setChatId(e.target.value)}
                placeholder="es. 123456789"
                className="rounded-none bg-zinc-900 border-white/10 mt-1 h-11 font-mono-data"
                data-testid="tg-chat-id-input"
                disabled={loading}
              />
              <div className="text-[11px] font-mono-data text-zinc-600 mt-1">
                Solo cifre. Nessun @, nessuno spazio.
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-white/5 pt-4">
              <div>
                <Label className="font-mono-data text-xs text-zinc-300">Abilita notifiche Telegram</Label>
                <div className="text-[11px] text-zinc-500 mt-0.5">Le zone salvate con "Telegram on" invieranno qui gli alert.</div>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                data-testid="tg-enabled-switch"
                disabled={loading}
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button
                onClick={save}
                disabled={saving || loading}
                className="rounded-none bg-lime-400 text-lime-950 hover:bg-lime-500 font-bold font-mono-data text-xs h-11 px-6"
                data-testid="tg-save-btn"
              >
                {saving ? <><CircleNotch size={14} weight="bold" className="animate-spin mr-2" />SALVATAGGIO...</> : "SALVA"}
              </Button>
              <Button
                onClick={test}
                disabled={testing || !chatId.trim()}
                variant="outline"
                className="rounded-none border-[#229ED9]/40 text-[#229ED9] hover:bg-[#229ED9]/10 font-mono-data text-xs h-11 px-6"
                data-testid="tg-test-btn"
              >
                {testing ? <><CircleNotch size={14} weight="bold" className="animate-spin mr-2" />INVIO...</> : <><PaperPlaneTilt size={14} weight="bold" className="mr-2" />INVIA TEST</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Alert Preferences */}
        <div className="border border-white/10 bg-zinc-900/30 mt-6" data-testid="alert-prefs-card">
          <div className="p-5 border-b border-white/10 flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-lime-400/10 border border-lime-400/30">
              <BellRinging size={20} weight="fill" className="text-lime-400" />
            </div>
            <div>
              <div className="font-display font-bold text-lg">Alert personalizzati</div>
              <div className="text-xs text-zinc-500">Scegli quali eventi generano una notifica</div>
            </div>
          </div>

          <div className="divide-y divide-white/5">
            <PrefRow
              icon={<Star size={16} weight="fill" className="text-lime-400" />}
              title="Conferma su salvataggio"
              subtitle="Ricevi una scheda analisi quando aggiungi un immobile o un'asta ai preferiti"
              testId="pref-watchlist-confirmation"
              checked={prefs.watchlist_confirmation}
              onChange={(v) => savePrefs({ watchlist_confirmation: v })}
              disabled={!enabled}
            />
            <PrefRow
              icon={<CircleNotch size={16} weight="bold" className="text-amber-400" />}
              title="Nuova asta in zona"
              subtitle="Ogni volta che un'asta compare in una zona salvata"
              testId="pref-new-auction"
              checked={prefs.new_auction}
              onChange={(v) => savePrefs({ new_auction: v })}
              disabled={!enabled}
            />
            <PrefRow
              icon={<House size={16} weight="fill" className="text-cyan-400" />}
              title="Nuovo immobile in zona"
              subtitle="Fires anche sulle inserzioni di vendita (non solo aste). Genera più notifiche."
              testId="pref-new-listing"
              checked={prefs.new_listing}
              onChange={(v) => savePrefs({ new_listing: v })}
              disabled={!enabled}
            />
            <PrefRow
              icon={<TrendDown size={16} weight="bold" className="text-red-400" />}
              title="Sconto aumentato"
              subtitle="Quando un'asta già vista viene ribassata (base d'asta più bassa = sconto maggiore)"
              testId="pref-discount-increased"
              checked={prefs.discount_increased}
              onChange={(v) => savePrefs({ discount_increased: v })}
              disabled={!enabled}
            />

            {prefs.discount_increased && (
              <div className="p-5" data-testid="pref-threshold-row">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="font-mono-data text-xs text-zinc-300 tracking-wide">SOGLIA RIBASSO MINIMO</Label>
                    <div className="text-[11px] text-zinc-500 mt-0.5">Notifica solo se lo sconto è aumentato di almeno questa percentuale</div>
                  </div>
                  <span className="font-mono-data text-lg text-lime-400 font-bold">+{Number(prefs.min_discount_increase_pct).toFixed(1)}pp</span>
                </div>
                <Slider
                  value={[Number(prefs.min_discount_increase_pct) || 5]}
                  onValueChange={(v) => setPrefs((p) => ({ ...p, min_discount_increase_pct: v[0] }))}
                  onValueCommit={(v) => savePrefs({ min_discount_increase_pct: v[0] })}
                  min={1} max={30} step={0.5}
                  disabled={!enabled}
                  data-testid="pref-threshold-slider"
                />
                <div className="flex justify-between mt-1 font-mono-data text-[10px] text-zinc-600">
                  <span>1pp (spam)</span>
                  <span>10pp</span>
                  <span>30pp (rari, forti ribassi)</span>
                </div>
              </div>
            )}
          </div>

          {!enabled && (
            <div className="p-4 border-t border-white/10 bg-zinc-900/60 text-xs font-mono-data text-zinc-500 flex items-center gap-2">
              <Info size={14} weight="bold" className="text-cyan-400" />
              Attiva prima le notifiche Telegram qui sopra per abilitare gli alert personalizzati.
            </div>
          )}
          {savingPrefs && (
            <div className="p-2 text-center font-mono-data text-[10px] text-lime-400/70 border-t border-white/5">
              <CircleNotch size={10} weight="bold" className="inline animate-spin mr-1" /> salvataggio...
            </div>
          )}
        </div>

        {/* Daily Digest */}
        <div className="border border-white/10 bg-zinc-900/30 mt-6" data-testid="digest-card">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-cyan-400/10 border border-cyan-400/30">
                <Clock size={20} weight="bold" className="text-cyan-400" />
              </div>
              <div>
                <div className="font-display font-bold text-lg">Digest giornaliero</div>
                <div className="text-xs text-zinc-500">Un solo riepilogo al giorno invece di una notifica per evento</div>
              </div>
            </div>
            {prefs.digest_enabled && (
              <Badge className="rounded-none bg-cyan-400 text-cyan-950 font-mono-data text-[10px] hover:bg-cyan-400" data-testid="digest-status-active">
                <CheckCircle size={10} weight="bold" className="mr-1" /> ORE {String(prefs.digest_hour).padStart(2, "0")}:00
              </Badge>
            )}
          </div>

          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-mono-data text-xs text-zinc-300">Abilita digest serale</Label>
                <div className="text-[11px] text-zinc-500 mt-0.5">Riceverai un solo messaggio Telegram + email con il riepilogo di tutte le nuove aste, ribassi e immobili nelle tue zone salvate</div>
              </div>
              <Switch
                checked={prefs.digest_enabled}
                onCheckedChange={(v) => savePrefs({ digest_enabled: v })}
                data-testid="digest-enabled-switch"
                disabled={loading}
              />
            </div>

            {prefs.digest_enabled && (
              <div className="border-t border-white/5 pt-4 space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="font-mono-data text-xs text-zinc-300 tracking-wide">ORARIO D'INVIO (Europe/Rome)</Label>
                    <span className="font-mono-data text-lg text-cyan-400 font-bold">{String(prefs.digest_hour).padStart(2, "0")}:00</span>
                  </div>
                  <Slider
                    value={[prefs.digest_hour]}
                    onValueChange={(v) => setPrefs((p) => ({ ...p, digest_hour: v[0] }))}
                    onValueCommit={(v) => savePrefs({ digest_hour: v[0] })}
                    min={0} max={23} step={1}
                    data-testid="digest-hour-slider"
                  />
                  <div className="flex justify-between mt-1 font-mono-data text-[10px] text-zinc-600">
                    <span>00:00</span>
                    <span>08:00</span>
                    <span>13:00</span>
                    <span>20:00</span>
                    <span>23:00</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                  <Button
                    onClick={loadPreview}
                    disabled={previewLoading}
                    variant="outline"
                    className="rounded-none border-cyan-400/40 text-cyan-400 hover:bg-cyan-400/10 font-mono-data text-xs h-10 px-4"
                    data-testid="digest-preview-btn"
                  >
                    {previewLoading ? <><CircleNotch size={12} weight="bold" className="animate-spin mr-2" />CARICO...</> : <><Eye size={12} weight="bold" className="mr-2" />ANTEPRIMA</>}
                  </Button>
                  <Button
                    onClick={sendDigestNow}
                    disabled={sendingDigest || !enabled}
                    className="rounded-none bg-cyan-400 text-cyan-950 hover:bg-cyan-500 font-bold font-mono-data text-xs h-10 px-4"
                    data-testid="digest-send-btn"
                  >
                    {sendingDigest ? <><CircleNotch size={12} weight="bold" className="animate-spin mr-2" />INVIO...</> : <><PaperPlaneTilt size={12} weight="bold" className="mr-2" />INVIA ADESSO</>}
                  </Button>
                  {!enabled && (
                    <span className="text-[11px] font-mono-data text-zinc-500 self-center">
                      · Attiva Telegram sopra per usare "Invia adesso"
                    </span>
                  )}
                </div>

                {preview && (
                  <div className="border border-white/10 bg-black/40 p-4 mt-2" data-testid="digest-preview-panel">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono-data text-[10px] text-cyan-400 tracking-widest">ANTEPRIMA · {preview.total_events} EVENTI</span>
                      <button onClick={() => setPreview(null)} className="text-zinc-500 hover:text-zinc-200 font-mono-data text-[10px]">CHIUDI</button>
                    </div>
                    {preview.total_events === 0 ? (
                      <div className="text-sm text-zinc-400">Nessun nuovo evento da riportare. Torna dopo che sono state pubblicate nuove aste o inserzioni.</div>
                    ) : (
                      <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-mono-data leading-relaxed max-h-64 overflow-auto">
                        {(preview.text || "").replace(/<\/?b>/g, "").replace(/<\/?i>/g, "")}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t border-white/10 grid grid-cols-3 divide-x divide-white/5">
            <div className="pr-3">
              <div className="font-mono-data text-[9px] text-zinc-500 tracking-widest mb-1">TELEGRAM</div>
              <div className="text-xs text-zinc-300">Un messaggio con emoji e formattazione</div>
            </div>
            <div className="px-3">
              <div className="font-mono-data text-[9px] text-zinc-500 tracking-widest mb-1">EMAIL</div>
              <div className="text-xs text-zinc-300">Copia testuale nella tua inbox</div>
            </div>
            <div className="pl-3">
              <div className="font-mono-data text-[9px] text-zinc-500 tracking-widest mb-1">DELTA</div>
              <div className="text-xs text-zinc-300">Solo eventi non riportati nei digest precedenti</div>
            </div>
          </div>
        </div>

        {/* PWA install card */}
        <div className="border border-white/10 bg-zinc-900/30 mt-6" data-testid="pwa-install-card">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-lime-400/10 border border-lime-400/30">
                <DeviceMobile size={20} weight="fill" className="text-lime-400" />
              </div>
              <div>
                <div className="font-display font-bold text-lg">Installa come app</div>
                <div className="text-xs text-zinc-500">Aggiungi ImmoScope alla home del tuo dispositivo</div>
              </div>
            </div>
            {isStandalone && (
              <Badge className="rounded-none bg-lime-400 text-lime-950 font-mono-data text-[10px] hover:bg-lime-400" data-testid="pwa-status-installed">
                <CheckCircle size={10} weight="bold" className="mr-1" /> INSTALLATA
              </Badge>
            )}
          </div>

          {isStandalone ? (
            <div className="p-5 text-sm text-zinc-300">
              Stai già usando ImmoScope come applicazione installata. Perfetto.
            </div>
          ) : isIos ? (
            <div className="p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Info size={14} weight="bold" className="text-cyan-400" />
                <span className="font-mono-data text-[10px] text-cyan-400 tracking-widest">SU IPHONE / IPAD</span>
              </div>
              <ol className="text-sm text-zinc-300 space-y-2 list-decimal list-inside marker:text-lime-400">
                <li>Apri questa pagina in <b>Safari</b> (non funziona in Chrome iOS)</li>
                <li>Tocca l'icona <Export size={14} weight="bold" className="inline text-cyan-400 -mt-0.5" /> <b>Condividi</b> in basso</li>
                <li>Scorri e tocca <Plus size={14} weight="bold" className="inline text-lime-400 -mt-0.5" /> <b>Aggiungi a Home</b></li>
                <li>Conferma con <b>Aggiungi</b> in alto a destra</li>
              </ol>
              <div className="text-[11px] font-mono-data text-zinc-500 border-t border-white/5 pt-3 mt-3">
                L'icona ImmoScope apparirà sulla home dell'iPhone e si aprirà a schermo intero.
              </div>
            </div>
          ) : (
            <div className="p-5 space-y-4">
              <div className="text-sm text-zinc-300 leading-relaxed">
                Se il tuo browser è compatibile, comparirà qui sotto un pulsante <b>Installa app</b>. Su Android/Chrome puoi anche usare il menu <span className="font-mono-data text-zinc-500">⋮</span> → <b>Installa app</b>. Su Windows/Mac Chrome, cerca l'icona a destra della barra degli indirizzi.
              </div>
              <div className="flex gap-3 items-center">
                <PwaInstallButton variant="full" />
                <div className="text-[11px] font-mono-data text-zinc-500">
                  Se non compare, apri il menu del browser e scegli "Installa ImmoScope".
                </div>
              </div>
            </div>
          )}

          <div className="px-5 py-3 border-t border-white/10 grid grid-cols-3 divide-x divide-white/5">
            <div className="pr-3">
              <div className="font-mono-data text-[9px] text-zinc-500 tracking-widest mb-1">OFFLINE</div>
              <div className="text-xs text-zinc-300">Interfaccia disponibile anche senza rete</div>
            </div>
            <div className="px-3">
              <div className="font-mono-data text-[9px] text-zinc-500 tracking-widest mb-1">FULLSCREEN</div>
              <div className="text-xs text-zinc-300">Si apre come app nativa, no barra browser</div>
            </div>
            <div className="pl-3">
              <div className="font-mono-data text-[9px] text-zinc-500 tracking-widest mb-1">HOME SCREEN</div>
              <div className="text-xs text-zinc-300">Icona ImmoScope sulla home del telefono</div>
            </div>
          </div>
        </div>

        {/* Info box */}
        <div className="mt-6 border border-white/10 p-4 text-xs font-mono-data text-zinc-500 leading-relaxed">
          <span className="text-lime-400">SUGGERIMENTO</span> · Per ricevere Telegram su una specifica zona, vai in <span className="text-lime-400">Alert</span>, edita/crea la zona e attiva "Telegram on". Il canale email resta indipendente.
        </div>
      </div>
    </div>
  );
}

function PrefRow({ icon, title, subtitle, checked, onChange, disabled = false, testId }) {
  return (
    <div className="p-5 flex items-start gap-4">
      <div className="w-8 h-8 shrink-0 flex items-center justify-center border border-white/10 bg-black/30">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <Label className="text-sm font-medium text-zinc-100 leading-tight">{title}</Label>
        <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">{subtitle}</div>
      </div>
      <Switch
        checked={!!checked}
        onCheckedChange={onChange}
        disabled={disabled}
        data-testid={testId}
      />
    </div>
  );
}

